import { VStack, HStack } from "@gluestack-ui/themed";
import { Text } from "@repo/core/components/core/Text";
import { parseParecer } from "./utils";
import { useMemo } from "react";

interface AnalysisParecerViewProps {
  parecer: string;
}

/**
 * Renders a value that may contain line breaks or list items
 */
const renderValue = (value: string) => {
  // Check if value contains line breaks (likely a list or multi-line content)
  if (value.includes("\n")) {
    const lines = value.split("\n").filter((line) => line.trim().length > 0);
    return (
      <VStack gap="$1">
        {lines.map((line, lineIndex) => {
          // Check if line is a list item (starts with "-" or "CEP:" or "FONE:")
          if (line.trim().startsWith("-") || line.includes(":")) {
            const colonIndex = line.indexOf(":");
            if (colonIndex > 0) {
              const label = line.substring(0, colonIndex).trim();
              const lineValue = line.substring(colonIndex + 1).trim();
              return (
                <HStack key={lineIndex} gap="$2" flexWrap="wrap">
                  <Text
                    fontSize="$xs"
                    fontWeight={500}
                    color="$textLight800"
                    flexShrink={0}
                  >
                    {label}:
                  </Text>
                  <Text fontSize="$xs" color="$textLight700" flex={1}>
                    {lineValue}
                  </Text>
                </HStack>
              );
            }
          }
          return (
            <Text
              key={lineIndex}
              fontSize="$xs"
              color="$textLight700"
              lineHeight="$md"
            >
              {line.trim()}
            </Text>
          );
        })}
      </VStack>
    );
  }

  return (
    <Text fontSize="$xs" color="$textLight700" lineHeight="$md">
      {value}
    </Text>
  );
};

export const AnalysisParecerView = ({ parecer }: AnalysisParecerViewProps) => {
  const parsedParecer = useMemo(() => parseParecer(parecer), [parecer]);

  if (!parecer || parecer.trim().length === 0) {
    return null;
  }

  // If parsing didn't produce structured sections, show raw text
  if (parsedParecer.sections.length === 0) {
    return (
      <VStack gap="$2">
        <Text fontSize="$xs" color="$textLight700" lineHeight="$md">
          {parecer}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack gap="$4">
      {parsedParecer.sections.map((section, sectionIndex) => (
        <VStack key={sectionIndex} gap="$2">
          {section.title && (
            <Text
              fontSize="$sm"
              fontWeight={600}
              color="$textLight900"
              textTransform="uppercase"
            >
              {section.title}
            </Text>
          )}
          <VStack gap="$1.5">
            {section.items.map((item, itemIndex) => {
              // Items without labels are usually continuation text or headers
              if (!item.label) {
                return (
                  <VStack key={itemIndex} mt={itemIndex > 0 ? "$2" : "$0"}>
                    {renderValue(item.value)}
                  </VStack>
                );
              }

              return (
                <HStack
                  key={itemIndex}
                  gap="$2"
                  flexWrap="wrap"
                  alignItems="flex-start"
                >
                  <Text
                    fontSize="$xs"
                    fontWeight={500}
                    color="$textLight800"
                    flexShrink={0}
                    minWidth="$20"
                  >
                    {item.label}:
                  </Text>
                  <VStack flex={1} gap="$0.5">
                    {renderValue(item.value)}
                  </VStack>
                </HStack>
              );
            })}
          </VStack>
        </VStack>
      ))}
    </VStack>
  );
};
